import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import {
  FORESTRY_PROJECT, BASELINE_CO2, COMMERCIAL_PURPOSE_OPTIONS_VALUES, COMMERCIAL_PURPOSE_OPTIONS
} from '../../../../../config';
import PlanningInterventionDialog from './PlanningInterventionDialog';
import AddIcon from '@mui/icons-material/Add';
import Fab from '@mui/material/Fab';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TargetProduct from '../TargetProduct';
import {useTranslation} from 'react-i18next';
import PlanningManagement from './PlanningManagement';
import PlanningInterventionTable from './PlanningInterventionTable';

//STYLES
const fab = {
  '&.MuiButtonBase-root': {
    borderRadius: '100%',
    backgroundColor: 'secondary.main',
    mr: 1,
    '&:hover': {
      backgroundColor: 'primary.main',
    }
  },
  '&.Mui-disabled': {
    backgroundColor: '#d7d7d7'
  }
};
const buttonsSx = (performances) => ({
  display: 'flex',
  height: '40px',
  flexDirection: 'row',
  justifyContent: performances.length ? 'space-between' : 'flex-start',
  gap: 4,
  pl: performances.length ? 3 : 0,
  mt: performances.length ? 2 : 0
});

const prunningsWeedingsSx = (planningInterventions) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: planningInterventions?.length ? 'space-between' : 'flex-start',
  width: '100%'
});

const containerSx = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '50vw'
};

const PlanningInterventionSection = ({
  absorptionBaseLine,
  absorptionProject,
  planningInterventions,
  hasRequiredValues,
  isOffline,
  mode,
  onPlanningInterventionChange,
  onCreateExanteProject
}) => {

  const {t} = useTranslation();
  const [isPerformancesMenuOpen, setPerformancesMenuOpen] = useState(false);
  const [withManagement, setWithManagement] = useState(mode !== BASELINE_CO2);
  const [fullyLoaded, setFullyLoaded] = useState(false);

  useEffect(() => {
    if (mode === BASELINE_CO2) {
      if (absorptionBaseLine.planting || absorptionBaseLine.final_cutting) {
        setWithManagement(true);
      }
    }
    setFullyLoaded(true);
  }, [absorptionBaseLine, absorptionProject]);

  const handleAddCO2Intervention = () => setPerformancesMenuOpen(!isPerformancesMenuOpen);

  const handlePruningChange = (e) => onPlanningInterventionChange({...planningInterventions, has_pruning: e.target.checked});
  const handleWeedingChange = (e) => onPlanningInterventionChange({...planningInterventions, has_weeding: e.target.checked});

  const disableBaselineCalculationButton = useMemo(() => (!hasRequiredValues || isOffline),
    [hasRequiredValues, isOffline, planningInterventions, withManagement]);

  const disableProjectCalculationButton = useMemo(() => (!hasRequiredValues || isOffline) ||
    !(absorptionBaseLine?.prediction) ||
    (
      !planningInterventions.planting &&
      !planningInterventions.intermediate_cuttings?.length &&
      !planningInterventions.final_cutting
    )
  , [hasRequiredValues, isOffline, absorptionBaseLine, planningInterventions]);

  const disableCalculationButton = useMemo(() => mode === BASELINE_CO2
    ? disableBaselineCalculationButton
    : disableProjectCalculationButton
  , [mode, hasRequiredValues, isOffline, disableBaselineCalculationButton, disableProjectCalculationButton]);

  const disableAddInterventionButton = isOffline || !withManagement;
  
  useEffect(() => fullyLoaded && !withManagement && onPlanningInterventionChange(
    {...planningInterventions, commercial_purpose: COMMERCIAL_PURPOSE_OPTIONS_VALUES.PAPER}
  ), [withManagement, fullyLoaded]);

  const handleChangeWithManagement = (is_managed) => {
    // Clean planting, intermediate_cuttings and final_cutting props when !withManagement
    if (!is_managed){
      const cleanedPLanningIntervention = {
        ...planningInterventions,
        planting: undefined,
        intermediate_cuttings: [],
        final_cutting: undefined
      };

      onPlanningInterventionChange(cleanedPLanningIntervention);
    }
    setWithManagement(is_managed);
  };

  const handleCreateExanteProject = () => onCreateExanteProject(planningInterventions);

  return <Box mb={2} sx={containerSx}>
    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2}}>
        { mode === BASELINE_CO2 && <PlanningManagement withManagement={withManagement} onManagementChange={handleChangeWithManagement}/> }
        { withManagement && <PlanningInterventionTable planningInterventions={planningInterventions} onPlanningInterventionChange={onPlanningInterventionChange}/> }
        <Box sx={prunningsWeedingsSx(planningInterventions)}>
          <Box ml={0.5}>
            <FormControlLabel
              label={t('Podas')}
              control={
                <Checkbox size='small'
                  checked={planningInterventions?.has_pruning}
                  disabled={!hasRequiredValues || isOffline}
                  onChange={handlePruningChange}/>
              }
            />
            <FormControlLabel
              label={t('Desbroces')}
              control={
                <Checkbox size='small'
                  checked={planningInterventions?.has_weeding}
                  disabled={!hasRequiredValues || isOffline}
                  onChange={handleWeedingChange}/>
              }
            />
          </Box>
          <Fab sx={fab} size='small' onClick={handleAddCO2Intervention} disabled={disableAddInterventionButton}>
            <AddIcon sx={{color: 'white'}}/>
          </Fab>
          <Box sx={buttonsSx(planningInterventions)}>
            <Button variant={disableCalculationButton ? 'outlined' : 'contained'}
              color='secondary' sx={{minWidth: '150px'}}
              disabled={disableCalculationButton}
              onClick={handleCreateExanteProject}>{t(' Calcular CO2')}</Button>
          </Box>
        </Box>
      </Box>
    </Box>
    <PlanningInterventionDialog isCO2InterventionsMenuOpen={isPerformancesMenuOpen} mode={mode} planningInterventions={planningInterventions}
      onPlanningInterventionChange={onPlanningInterventionChange}
      onDialogClose={() => setPerformancesMenuOpen(!isPerformancesMenuOpen)}/>
    <TargetProduct
      selectedProductId={planningInterventions.commercial_purpose}
      options={!withManagement ? [COMMERCIAL_PURPOSE_OPTIONS[1]]: undefined}
      onProductChange={
        (value) => onPlanningInterventionChange(
          {...planningInterventions, commercial_purpose: withManagement ? value : COMMERCIAL_PURPOSE_OPTIONS_VALUES.PAPER}
        )
      }/>
  </Box>;
};

PlanningInterventionSection.propTypes = {
  absorptionBaseLine: PropTypes.object,
  absorptionProject: PropTypes.object,
  planningInterventions: PropTypes.object,
  mode: PropTypes.oneOf([BASELINE_CO2, FORESTRY_PROJECT]).isRequired,
  hasRequiredValues: PropTypes.bool,
  isOffline: PropTypes.bool,
  onPlanningInterventionChange: PropTypes.func.isRequired,
  onCreateExanteProject: PropTypes.func.isRequired
};

export default PlanningInterventionSection;